/**
 * Landing page content derived from translations. Kept free of React so the
 * build step (scripts/generate-seo-pages.js) renders the exact same copy and
 * structured data into the static, crawlable HTML.
 */
import { formatCurrency } from '../../utils/currencyUtils';
import {
  getLocalizedMarketingUrl,
  getMarketingOgImageUrl,
  getMarketingUrl,
} from '../../constants/marketingSeo';
import { LANDING_PRICING, LANDING_PRICING_CURRENCY } from '../../constants/LandingPricing';
import { LEGAL_ENTITY } from '../../constants/LegalEntity';

export const FEATURE_KEYS = [
  'appointments',
  'booking',
  'team',
  'services',
  'memberships',
  'customers',
  'reminders',
  'payroll',
  'dashboard',
];

export const STEP_KEYS = ['register', 'setup', 'team', 'book'];
export const HERO_SPEC_KEYS = ['free', 'pro', 'card', 'commitment'];
export const FAQ_KEYS = ['price', 'plans', 'messaging', 'healthBeauty', 'multiStore', 'creditCard'];

/**
 * `value` rows render plan-specific wording, `soon` marks an announced feature.
 * `null` hides the row for that plan.
 */
const PLAN_FEATURE_ROWS = [
  { key: 'stores', free: 'value', pro: 'value' },
  { key: 'professionals', free: 'value', pro: 'value' },
  { key: 'appointments', free: true, pro: true },
  { key: 'emailReminders', free: true, pro: true },
  { key: 'realtimeChat', free: true, pro: true },
  { key: 'dashboard', free: 'value', pro: 'value' },
  { key: 'payments', free: 'value', pro: 'value' },
  { key: 'deposits', free: false, pro: true },
  { key: 'installments', free: false, pro: true },
  { key: 'refunds', free: false, pro: true },
  { key: 'productsStock', free: false, pro: true },
  { key: 'membershipPlans', free: false, pro: true },
  { key: 'teamPayroll', free: false, pro: true },
  { key: 'waitlistHours', free: false, pro: true },
  { key: 'additionalCosts', free: false, pro: true },
  { key: 'customPermissions', free: false, pro: true },
  { key: 'smsReminders', free: false, pro: true, rate: 'sms' },
  { key: 'whatsappReminders', free: false, pro: true, rate: 'whatsapp' },
  { key: 'customBranding', free: false, pro: true },
  { key: 'prioritySupport', free: false, pro: true },
  { key: 'marketingAutomation', free: null, pro: 'soon' },
];

export const buildPlanRows = (planKey, t, messageRates) =>
  PLAN_FEATURE_ROWS.reduce((rows, row) => {
    const state = row[planKey];
    if (state === null) {
      return rows;
    }

    const included = state !== false;
    rows.push({
      key: row.key,
      included,
      comingSoon: state === 'soon',
      label:
        state === 'value'
          ? t(`landing.pricing.plans.${planKey}.values.${row.key}`)
          : t(`landing.pricing.features.${row.key}`),
      note:
        included && row.rate
          ? t('landing.pricing.rates.perMessage', { price: messageRates[row.rate] })
          : null,
    });

    return rows;
  }, []);

export const getLandingPrices = () => ({
  introPrice: formatCurrency(LANDING_PRICING.introductoryMonthly, LANDING_PRICING_CURRENCY),
  basePrice: formatCurrency(LANDING_PRICING.baseSubscriptionMonthly, LANDING_PRICING_CURRENCY),
  teamMemberPrice: formatCurrency(LANDING_PRICING.additionalTeamMemberMonthly, LANDING_PRICING_CURRENCY),
  extraStorePrice: formatCurrency(LANDING_PRICING.additionalStoreMonthly, LANDING_PRICING_CURRENCY),
  smsPrice: formatCurrency(LANDING_PRICING.smsPerMessage, LANDING_PRICING_CURRENCY),
  whatsAppPrice: formatCurrency(LANDING_PRICING.whatsAppPerMessage, LANDING_PRICING_CURRENCY),
});

export const buildPricingFootnote = (t, prices) =>
  t('landing.pricing.footnote', {
    introPrice: prices.introPrice,
    regularPrice: prices.basePrice,
    extraStorePrice: prices.extraStorePrice,
    teamMemberPrice: prices.teamMemberPrice,
    smsPrice: prices.smsPrice,
    whatsAppPrice: prices.whatsAppPrice,
    months: LANDING_PRICING.introductoryPeriodMonths,
  });

export const buildFaqItems = (t, prices) =>
  FAQ_KEYS.map((key) => ({
    question: t(`landing.faq.items.${key}.question`),
    answer: t(`landing.faq.items.${key}.answer`, {
      introPrice: prices.introPrice,
      regularPrice: prices.basePrice,
      teamMemberPrice: prices.teamMemberPrice,
      extraStorePrice: prices.extraStorePrice,
      smsPrice: prices.smsPrice,
      whatsAppPrice: prices.whatsAppPrice,
      months: LANDING_PRICING.introductoryPeriodMonths,
    }),
  }));

export const buildOrganizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${getMarketingUrl('/')}#organization`,
  name: LEGAL_ENTITY.productName,
  url: getMarketingUrl('/'),
  logo: getMarketingUrl('/logo512.png'),
  email: LEGAL_ENTITY.supportEmail,
  address: { '@type': 'PostalAddress', addressCountry: 'PT' },
});

export const buildWebSiteJsonLd = (lang) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${getMarketingUrl('/')}#website`,
  name: LEGAL_ENTITY.productName,
  url: getLocalizedMarketingUrl('/', lang),
  inLanguage: lang,
  publisher: { '@id': `${getMarketingUrl('/')}#organization` },
});

/** Default structured data for any other marketing page. */
export const buildWebPageJsonLd = ({ title, description, path, lang }) => [
  buildOrganizationJsonLd(),
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: getLocalizedMarketingUrl(path, lang),
    inLanguage: lang,
    isPartOf: { '@id': `${getMarketingUrl('/')}#website` },
  },
];

/** Structured data for the landing page: organization, site, product offers and FAQ. */
export const buildLandingJsonLd = (t, lang, faqItems) => {
  const pageUrl = getLocalizedMarketingUrl('/', lang);

  return [
    buildOrganizationJsonLd(),
    buildWebSiteJsonLd(lang),
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: LEGAL_ENTITY.productName,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web, Android, iOS',
      url: pageUrl,
      image: getMarketingOgImageUrl(lang),
      description: t('landing.seo.description'),
      inLanguage: lang,
      publisher: { '@id': `${getMarketingUrl('/')}#organization` },
      featureList: FEATURE_KEYS.map((key) => t(`landing.features.${key}.title`)),
      offers: [
        {
          '@type': 'Offer',
          name: t('landing.pricing.plans.free.name'),
          description: t('landing.pricing.plans.free.tagline'),
          price: '0',
          priceCurrency: LANDING_PRICING_CURRENCY,
          url: pageUrl,
        },
        {
          '@type': 'Offer',
          name: t('landing.pricing.plans.pro.name'),
          description: t('landing.pricing.plans.pro.tagline'),
          price: LANDING_PRICING.introductoryMonthly.toFixed(2),
          priceCurrency: LANDING_PRICING_CURRENCY,
          url: pageUrl,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: lang,
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    },
  ];
};
